import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import lang from '@/lang'
import { transactionTypes, bookingAssignmentsProceedingTypes } from '@/modules/base/util/constants'
import { Title } from '@/modules/base/components/texts/index'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { CurrencyInput, DateInput, SelectInput, TextInput, RadioInput } from '@/modules/base/components/inputs'
import TooltipAssignBookingImage from '@assets/images/tooltipAssignBooking.svg?url'
import moment from 'moment'
import clsx from 'clsx'
import { PAYMENT_TYPE_LESS_BANKCHARGE } from '@/modules/payment/constants'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import parseHtmlWithReactLinks from '@/utils/parseHtmlWithReactLinks'

const {
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_CASH_CREDIT_NOTE,
  TRANSACTION_TYPE_REVENUE,
  TRANSACTION_TYPE_EXPENSE,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
} = transactionTypes

const creditNoteTypes = [
  TRANSACTION_TYPE_CASH_CREDIT_NOTE,
  TRANSACTION_TYPE_CREDIT_NOTE,
]

const {
  SURCHARGE,
  PARTIAL,
  BANK_CHARGE,
  DISCOUNT,
  BOOK_LATER,
} = bookingAssignmentsProceedingTypes

const getOptions = (difference, amount, transactionType) => {
  const isExpenseOrRevenue = [TRANSACTION_TYPE_REVENUE, TRANSACTION_TYPE_EXPENSE].includes(transactionType)
  if ((difference > 0 && amount < 0) || (difference < 0 && amount >= 0)) {
    return [
      { label: `<b>${lang.booking.assignDocument.proceedingOptions[SURCHARGE]}</b> ${lang.booking.assignDocument[isExpenseOrRevenue ? 'proceedingOptionsDescriptionExpense' : 'proceedingOptionsDescription'][SURCHARGE]}`, value: SURCHARGE, dangerousInnerHtml: true },
      { label: `<b>${lang.booking.assignDocument.proceedingOptions[BOOK_LATER]}</b> ${lang.booking.assignDocument[isExpenseOrRevenue ? 'proceedingOptionsDescriptionExpense' : 'proceedingOptionsDescription'][BOOK_LATER]}`, value: BOOK_LATER, dangerousInnerHtml: true },
    ]
  } else {
    return [
      { label: `<b>${lang.booking.assignDocument.proceedingOptions[PARTIAL]}</b> ${lang.booking.assignDocument[isExpenseOrRevenue ? 'proceedingOptionsDescriptionExpense' : 'proceedingOptionsDescription'][PARTIAL]}`, value: PARTIAL, dangerousInnerHtml: true },
      { label: `<b>${lang.booking.assignDocument.proceedingOptions[DISCOUNT]}</b> ${lang.booking.assignDocument[isExpenseOrRevenue ? 'proceedingOptionsDescriptionExpense' : 'proceedingOptionsDescription'][DISCOUNT]}`, value: DISCOUNT, dangerousInnerHtml: true },
      { label: `<b>${lang.booking.assignDocument.proceedingOptions[BANK_CHARGE]}</b> ${lang.booking.assignDocument[isExpenseOrRevenue ? 'proceedingOptionsDescriptionExpense' : 'proceedingOptionsDescription'][BANK_CHARGE]}`, value: PAYMENT_TYPE_LESS_BANKCHARGE, dangerousInnerHtml: true },
    ]
  }
}

const useStyles = makeStyles(theme => ({
  hint: {
    fontSize: 12,
    color: theme.darkGrey,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '30px 0',

    '&:first-of-type': {
      marginBottom: 15,
    },
    '&:last-child': {
      marginBottom: 0,
    },

    '& > *': {
      flex: '1 1 0',
      margin: '0 8px',

      '&:first-child': {
        marginLeft: 0,
      },
      '&:last-child': {
        marginRight: 0,
      },

    },
  },
  differenceWrapper: {
    transition: '.1s ease-out',
    flexGrow: 1,
    height: 0,
    '&.show': {
      height: '100%',
    },
  },
  differenceInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 20,
    fontSize: theme.fontSizes.smaller,
  },
  differenceTitle: {
    fontSize: theme.fontSizes.default,
    marginBottom: 10,
  },
  highlight: {
    color: theme.primary,
  },
  radioInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  tooltip: {
    height: 'fit-content',
    color: theme.black,
  },
  tooltipTitle: {
    paddingBottom: '20px',
    textAlign: 'center',
    fontSize: 14,
    lineHeight: '20px',
    color: theme.black,
    fontWeight: theme.fontWeights.bold,
  },
}))

const PaymentForm = props => {
  const classes = useStyles(props)
  const {
    data,
    bankAccounts,
    dunning,
    onFormDataChange,
    hideTitle,
    hideInvoiceInfo,
    difference,
    proceeding,
    onProceedingClick,
    readOnly,
  } = props

  const {
    transactionNumber,
    customerName,
    date,
    amount,
    notes,
    bankAccountId,
    transactionType,
  } = data

  const handleBankAccountChange = (option) => {
    const { value } = option
    onFormDataChange(value, 'bankAccountId')
  }

  const bankOptions = bankAccounts.map(account => {
    const { id, accountName } = account
    return { value: id, label: accountName }
  })
  const dunningHint = dunning
    ? <span className={classes.hint}>[{lang.paymentModal.dunning.hint}]<b>{dunning.label}</b> ({lang.paymentModal.dunning.label}: {formatCurrency(dunning.charge)})</span>
    : null

  const title = creditNoteTypes.includes(transactionType) ? lang.paymentModal.titleCreditNote : lang.paymentModal.titleInvoice
  const labelTransactionNumberInput = creditNoteTypes.includes(transactionType) ? lang.paymentLabelTransactionNumberInputCreditNote : lang.paymentLabelTransactionNumberInputInvoice
  const isCreditNote = [TRANSACTION_TYPE_CASH_CREDIT_NOTE, TRANSACTION_TYPE_CREDIT_NOTE, TRANSACTION_TYPE_MANUAL_CREDIT_NOTE].includes(transactionType)
  const showBalanceFooter = !!difference

  const options = getOptions(difference, amount, transactionType)
  if (showBalanceFooter && (!proceeding || !options.map(({ value }) => value).includes(proceeding))) {
    onProceedingClick(options[0].value)
  }

  const AssignBookingTooltip = (<div className={classes.tooltip}>
    <div className={classes.tooltipTitle}>{lang.paymentModal.addAccountNoteToolTip}</div>
    <SVGIcon svg={TooltipAssignBookingImage} />
  </div>)

  const paymentModalLabels = [TRANSACTION_TYPE_REVENUE, TRANSACTION_TYPE_EXPENSE].includes(transactionType) ? lang.paymentModal.labelExpense : lang.paymentModal.label

  return (
    <div className={classes.container}>
      {!hideTitle && <Title>{title}</Title>}

      {!hideInvoiceInfo && <div className={classes.row}>
        <TextInput
          name='transactionNumber'
          label={labelTransactionNumberInput}
          value={transactionNumber}
          onChange={onFormDataChange}
          disabled={true}
          required
        />
        <TextInput
          name='customerName'
          label={paymentModalLabels.customer}
          value={customerName} config
          onChange={onFormDataChange}
          disabled={true}
          required
        />
      </div>}
      <div className={classes.hint}>
        {dunningHint}
      </div>
      <div className={classes.row}>
        <DateInput
          name='date'
          label={!isCreditNote ? paymentModalLabels.date : lang.paymentModal.label.creditNoteDate}
          value={date || moment()}
          onChange={onFormDataChange}
          readOnly={readOnly}
          required
        />
        <CurrencyInput
          name='amount'
          autoFocus
          label={paymentModalLabels.amount}
          value={amount}
          onChange={onFormDataChange}
          readOnly={readOnly}
          required
        />
      </div>
      <div className={clsx(classes.differenceWrapper, { show: showBalanceFooter })}>
        {showBalanceFooter && <div className={classes.differenceInnerWrapper}>
          <div className={classes.differenceTitle}>
            {lang.booking.assignDocument.differenceOf} <span className={classes.highlight}>{formatCurrency(Math.abs(difference))}</span> {lang.booking.assignDocument.treatAs}
          </div>
          <RadioInput
            customOptionContainerClass={classes.radioInputWrapper}
            options={options}
            value={proceeding}
            onChange={(proceeding) => onProceedingClick(proceeding)}
          />
        </div>}
      </div>
      <div className={classes.hint}>
        {lang.paymentModal.addAccountNoteBegin}
        <RichTooltip content={AssignBookingTooltip} width={360} placement={'top'}>
          <a>{lang.paymentModal.addAssignment}</a>
        </RichTooltip>
        <span>{parseHtmlWithReactLinks(lang.paymentModal.addAccountNote(transactionType))}</span>
      </div>
      <div className={classes.row}>
        <SelectInput
          notAsync
          allowCreate={false}
          name='targetBankAccount'
          label={paymentModalLabels.targetBankAccount}
          value={bankAccountId}
          options={bankOptions}
          handleChange={handleBankAccountChange}
          customSelectOptions={{
            menuPosition: 'fixed',
          }}
          readOnly={readOnly}
          required
        />
      </div>
      <div className={classes.row}>
        <TextInput
          name='notes'
          label={paymentModalLabels.notes}
          value={notes}
          onChange={onFormDataChange}
          readOnly={readOnly}
        />
      </div>
    </div>
  )
}

PaymentForm.propTypes = {
  model: PropTypes.object,
  dunningObj: PropTypes.object,
  bankAccounts: PropTypes.array,
  hideTitle: PropTypes.bool,
  hideInvoiceInfo: PropTypes.bool,
  readOnly: PropTypes.bool,
}

PaymentForm.defaultProps = {
  readOnly: false,
}

export default PaymentForm
